<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import InboxReconnectionRequired from '../components/InboxReconnectionRequired.vue';
import wameSocialChannel from 'dashboard/api/channel/wameSocialChannel';
import WameAuthorizationFrame from './WameAuthorizationFrame.vue';

// Banner de reconexão de Instagram e Messenger. Os dois refazem a autorização
// pela mesma página da WAME, que reconhece a instância pela chave.
const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const isRequesting = ref(false);
// A reautorização também deixou de ser outra página: desenha aqui, embaixo do
// banner. Ver WameAuthorizationFrame.
const authorizationUrl = ref('');
const isPending = computed(() => !props.inbox.wame_connected);
const providerTranslationKey = computed(() =>
  props.inbox.wame_provider === 'instagram' ? 'INSTAGRAM' : 'MESSENGER'
);
const actionLabel = computed(() => {
  if (!isPending.value) return null;

  return t(`INBOX_MGMT.ADD.WAME_SOCIAL.${providerTranslationKey.value}.CTA`);
});
const description = computed(() => {
  if (!isPending.value) return null;

  return t(`INBOX_MGMT.ADD.WAME_SOCIAL.${providerTranslationKey.value}.HELP`);
});

const reauthorize = async () => {
  isRequesting.value = true;
  try {
    const { data } = await wameSocialChannel.reauthorize(
      props.inbox.id,
      window.location.href.split('?')[0]
    );
    authorizationUrl.value = data.payload.authorization_url;
    isRequesting.value = false;
  } catch (error) {
    useAlert(t('INBOX_MGMT.ADD.WAME_SOCIAL.ERROR_CONNECT'));
    isRequesting.value = false;
  }
};

// No Messenger o iframe avisa aqui mesmo que terminou; recarregar a tela é o
// jeito honesto de refletir o novo estado do canal, que é lido no servidor.
const onAuthorized = () => {
  window.location.reload();
};
</script>

<template>
  <div>
    <InboxReconnectionRequired
      v-if="!authorizationUrl"
      class="mx-6"
      :action-label="actionLabel"
      :description="description"
      @reauthorize="reauthorize"
    />

    <WameAuthorizationFrame
      v-else
      class="mx-6"
      :url="authorizationUrl"
      :provider="inbox.wame_provider"
      @connected="onAuthorized"
    />
  </div>
</template>
